import React, { useState } from 'react';

import { DeleteOutlined, SendOutlined, LinkOutlined, InboxOutlined, UserOutlined } from '@ant-design/icons';
import { Table, Button, Space, Typography, Tooltip, Modal, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';

import staffService from '../../service';
import { Staff, StaffListProps } from '../../types';

const StaffList = ({ data, eventData, setIsModalOpen, onRefresh, loadingStaffList }: StaffListProps) => {
  const isEventFinished = eventData?.is_past_event;
  const [copiedTooltip, setCopiedTooltip] = useState<{ [key: string]: boolean }>({});
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});

  const sortedData = [...data].sort((a, b) => {
    if (a.is_owner && !b.is_owner) return -1;
    if (!a.is_owner && b.is_owner) return 1;
    return 0;
  });

  const showDeleteConfirm = (eventId: string, staffId: string) => {
    Modal.confirm({
      icon: <DeleteOutlined style={{ color: 'black' }} />,
      centered: true,
      title: 'Tem certeza que deseja remover este credenciador da lista?',
      content: 'Este credenciador não poderá mais acessar o leitor de QR Codes para realizar check-ins neste evento.',
      okText: 'Remover',
      okType: 'primary',
      cancelText: 'Cancelar',
      okButtonProps: {
        style: { backgroundColor: '#ffbc00', color: '#000' },
        onFocus: e => (e.target.style.outline = '3px solid #ffe97a'),
        onBlur: e => (e.target.style.outline = 'none')
      },
      cancelButtonProps: {
        style: { backgroundColor: '#fff', color: '#000', borderColor: '#000' },
        onFocus: e => (e.target.style.outline = '3px solid #ffe97a'),
        onBlur: e => (e.target.style.outline = 'none')
      },
      onOk() {
        handleDeleteStaff(eventId, staffId);
      }
    });
  };

  const handleDeleteStaff = (eventId: string, staffId: string) => {
    const subscription = staffService.deleteEventStaff(eventId, staffId).subscribe({
      next: () => {
        notification.success({ message: 'Credenciador removido com sucesso!' });
        onRefresh();
      },
      error: error => {
        notification.error({ message: 'Erro ao remover credenciador.' });
        console.error('Delete staff error:', error);
      }
    });

    return () => subscription.unsubscribe();
  };

  const handleResendAccessLinkStaff = (eventId: string, staffId: string) => {
    const key = `${eventId}-${staffId}`;
    setIsLoading(prev => ({ ...prev, [key]: true }));
    const subscription = staffService.resendAccessLinkEventStaff(eventId, staffId).subscribe({
      next: () => {
        notification.success({ message: 'Convite de acesso reenviado com sucesso!' });
        setIsLoading(prev => ({ ...prev, [key]: false }));
      },
      error: error => {
        setIsLoading(prev => ({ ...prev, [key]: false }));
        notification.error({ message: 'Erro ao reenviar convite.' });
        console.error('resend access link staff error:', error);
      }
    });

    return () => subscription.unsubscribe();
  };

  const columns: ColumnsType<Staff> = [
    {
      title: 'Credenciador',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (text: string, record: Staff) => (
        <span>
          {record.is_owner && <UserOutlined style={{ marginRight: 8 }} />}
          {text}
        </span>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '30%'
    },
    {
      title: 'Checkins Realizados',
      dataIndex: 'checkins',
      key: 'checkins',
      align: 'right',
      width: '20%'
    },
    {
      title: 'Ações',
      key: 'actions',
      align: 'right',
      width: '25%',
      render: (_: any, record: Staff) => {
        const isDisabled = record.is_owner || isEventFinished;
        const tooltipMessage = record.is_owner
          ? 'Ações não disponíveis para o organizador'
          : isEventFinished
            ? 'Evento encerrado'
            : '';
        const loadingKey = `${eventData.id}-${record.id}`;
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              <Tooltip title={isDisabled ? tooltipMessage : 'Reenviar convite por e-mail'} color='#ffbc00'>
                <span>
                  <Button
                    icon={<SendOutlined />}
                    loading={isLoading[loadingKey] || false}
                    disabled={isDisabled}
                    onClick={() => handleResendAccessLinkStaff(eventData.id, record.id)}
                  />
                </span>
              </Tooltip>

              <Tooltip title={isDisabled ? tooltipMessage : 'Remover Credenciador'} color='#ffbc00'>
                <span>
                  <Button
                    icon={<DeleteOutlined />}
                    disabled={isDisabled}
                    onClick={() => showDeleteConfirm(eventData.id, record.id)}
                  />
                </span>
              </Tooltip>

              <Tooltip
                title={isDisabled ? tooltipMessage : copiedTooltip[record.id] ? 'Copiado!' : 'Copiar link do convite'}
                trigger={copiedTooltip[record.id] ? 'click' : 'hover'}
                color='#ffbc00'
                onOpenChange={visible => {
                  if (!visible && copiedTooltip[record.id]) {
                    setCopiedTooltip(prev => ({
                      ...prev,
                      [record.id]: false
                    }));
                  }
                }}
              >
                <span>
                  <Button
                    icon={<LinkOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(record.url);
                      setCopiedTooltip(prev => ({
                        ...prev,
                        [record.id]: true
                      }));
                    }}
                    disabled={isDisabled}
                  />
                </span>
              </Tooltip>
            </Space>
          </div>
        );
      }
    }
  ];

  return (
    <Table
      loading={loadingStaffList}
      dataSource={sortedData}
      columns={columns}
      rowKey='id'
      pagination={false}
      locale={{
        emptyText: !loadingStaffList && (
          <div style={{ textAlign: 'center' }}>
            <Space direction='vertical' size='middle'>
              <InboxOutlined style={{ color: '#000', fontSize: '38px' }} />
              <Typography.Title style={{ fontSize: '16px' }}>Nenhum credenciador cadastrado ainda</Typography.Title>
              <Typography.Text>
                Você ainda não definiu quem vai fazer a leitura dos ingressos no dia do evento. <br />
                Cadastre um credenciador para garantir um check-in rápido e organizado.
              </Typography.Text>
              <Button type='default' onClick={() => setIsModalOpen(true)}>
                Adicionar Credenciador
              </Button>
            </Space>
          </div>
        )
      }}
    />
  );
};

export default StaffList;
